import React, {
  useEffect,
  useState,
  useMemo,
  useCallback
} from "react";
import { createPortal } from "react-dom";
import "../styles/AdminOrder.css";

import {
  ORDERS_API,
  SUBSCRIBERS_API
} from "../config/api";
import { showConfirm } from "../utils/customDialog";

// ================= MODULE LEVEL LABEL HELPERS =================
const getDateString = (dateVal) => {
  if (!dateVal) return null;
  if (typeof dateVal === "string") {
    const trimmed = dateVal.trim();
    const ymdMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymdMatch) {
      if (trimmed.includes("T") || trimmed.includes("Z")) {
        const d = new Date(trimmed);
        if (!isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${y}-${m}-${day}`;
        }
      }
      return `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`;
    }
    const dmyMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (dmyMatch) {
      return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
    }
  }
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getSubActivityDates = (sub) => {
  if (!sub) return [];
  const rawDates = [
    sub.latest_activity_date,
    sub.receipt_date,
    sub.created_at,
    sub.last_renewed_at,
    sub.resumed_at
  ];
  const dates = new Set();
  rawDates.forEach((val) => {
    const ds = getDateString(val);
    if (ds) dates.add(ds);
  });
  return Array.from(dates);
};

const isOrderDispatchable = (o) => {
  if (!o) return false;
  const pStatus = String(o.payment_status || "").trim().toLowerCase();
  const oStatus = String(o.order_status || "").trim().toLowerCase();
  if (pStatus === "failed" || oStatus === "cancelled") return false;
  const isValidStatus = (
    pStatus === "success" ||
    pStatus === "paid" ||
    pStatus === "completed" ||
    oStatus === "processing" ||
    oStatus === "shipping" ||
    oStatus === "pending" ||
    oStatus === "placed"
  );
  const hasAddress = Boolean(
    (o.address && o.address.trim()) ||
    (o.city && o.city.trim()) ||
    (o.pincode && o.pincode.trim())
  );
  return isValidStatus && hasAddress;
};

const getOrderDispatchMode = (o) => {
  if (!o) return "courier";
  const cp = String(o.courier_partner || "").toLowerCase();
  if (cp.includes("speed")) return "speed_post";
  if (cp.includes("book")) return "book_post";
  if (cp.includes("direct") || cp.includes("hand")) return "direct";
  if (cp.includes("air mail") || cp.includes("international")) return "air_mail";
  return "courier";
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("paid_only");

  const [expandedOrder, setExpandedOrder] = useState(null);
  const [viewingReceiptOrder, setViewingReceiptOrder] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);

  const [actionLoading, setActionLoading] = useState(null);

  const getTodayISO = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getYesterdayISO = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const [showPrintLabelModal, setShowPrintLabelModal] = useState(false);
  const [printDateScope, setPrintDateScope] = useState("today"); // 'today' | 'yesterday' | 'specific_date' | 'date_range' | 'all'
  const [printTargetDate, setPrintTargetDate] = useState(getTodayISO());
  const [printFromDate, setPrintFromDate] = useState(getYesterdayISO());
  const [printToDate, setPrintToDate] = useState(getTodayISO());
  const [printLabelFilter, setPrintLabelFilter] = useState("daily_combined");
  const [printDispatchFilter, setPrintDispatchFilter] = useState("all");
  const [printBatchLimit, setPrintBatchLimit] = useState("all");
  const [customStartPage, setCustomStartPage] = useState(1);
  const [customEndPage, setCustomEndPage] = useState(20);
  const [printingLabels, setPrintingLabels] = useState(false);
  const [subscribersCache, setSubscribersCache] = useState([]);
  const [loadingSubscribersCache, setLoadingSubscribersCache] = useState(false);
  const [selectedMemberKeys, setSelectedMemberKeys] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");

  // Calibrated sticker sheet alignment (in cm)
  // Physical Sheet: 20.9cm x 29.81cm | 3 cols x 7 rows (6.3cm x 3.8cm) | Col gap: 0.25cm | Row gap: 0cm
  // Default 2.35cm top border = 1.60cm physical top border + 0.75cm printer upward-feed offset compensation
  // so every row's content prints at exact Sticker Top + 0.2cm (1.8cm, 5.6cm, 9.4cm, 13.2cm, 17.0cm, 20.8cm, 24.6cm)
  const [labelTopMargin, setLabelTopMargin] = useState(() => {
    try {
      const saved = localStorage.getItem("vaithikasri_label_top_margin_v3_cm");
      if (!saved) return 2.35;
      const val = parseFloat(saved);
      return isNaN(val) ? 2.35 : val;
    } catch (e) {
      return 2.35;
    }
  });
  const [labelLeftMargin, setLabelLeftMargin] = useState(() => {
    try {
      const saved = localStorage.getItem("vaithikasri_label_left_margin_v3_cm");
      if (!saved) return 0.7;
      const val = parseFloat(saved);
      return isNaN(val) ? 0.7 : val;
    } catch (e) {
      return 0.7;
    }
  });
  const [showCalibrationSettings, setShowCalibrationSettings] = useState(false);

  const updateTopMargin = (val) => {
    const num = Math.max(0, Math.min(5, Math.round(val * 100) / 100));
    setLabelTopMargin(num);
    try { localStorage.setItem("vaithikasri_label_top_margin_v3_cm", String(num)); } catch (e) {}
  };

  const updateLeftMargin = (val) => {
    const num = Math.max(0, Math.min(3, Math.round(val * 100) / 100));
    setLabelLeftMargin(num);
    try { localStorage.setItem("vaithikasri_label_left_margin_v3_cm", String(num)); } catch (e) {}
  };

  const resetLabelMargins = () => {
    updateTopMargin(2.35);
    updateLeftMargin(0.7);
  };

  const isSubscriberInDateScope = useCallback((sub) => {
    if (printDateScope === "all") return true;
    const dates = getSubActivityDates(sub);
    if (dates.length === 0) return false;

    if (printDateScope === "today") {
      return dates.includes(getTodayISO());
    }
    if (printDateScope === "yesterday") {
      return dates.includes(getYesterdayISO());
    }
    if (printDateScope === "specific_date") {
      return dates.includes(printTargetDate);
    }
    if (printDateScope === "date_range") {
      if (!printFromDate || !printToDate) return true;
      return dates.some((d) => d >= printFromDate && d <= printToDate);
    }
    return true;
  }, [printDateScope, printTargetDate, printFromDate, printToDate]);

  const isOrderInDateScope = useCallback((order) => {
    if (printDateScope === "all") return true;
    const dStr = getDateString(order.created_at || order.updated_at);
    if (!dStr) return false;

    if (printDateScope === "today") {
      return dStr === getTodayISO();
    }
    if (printDateScope === "yesterday") {
      return dStr === getYesterdayISO();
    }
    if (printDateScope === "specific_date") {
      return dStr === printTargetDate;
    }
    if (printDateScope === "date_range") {
      if (!printFromDate || !printToDate) return true;
      return dStr >= printFromDate && dStr <= printToDate;
    }
    return true;
  }, [printDateScope, printTargetDate, printFromDate, printToDate]);

  const getSubscriberDispatchMode = useCallback((sub) => {
    if (!sub) return "general_post";

    // 0. Direct match if backend returned standardized dispatch_mode
    if (sub.dispatch_mode) {
      const dm = String(sub.dispatch_mode).trim().toLowerCase();
      if (dm.includes("direct") || dm.includes("hand")) return "direct";
      if (dm.includes("speed")) return "speed_post";
      if (dm.includes("book")) return "book_post";
      if (dm.includes("courier")) return "courier";
      if (dm.includes("air mail") || dm.includes("international")) return "air_mail";
      if (dm.includes("general")) return "general_post";
    }

    const cp = (sub.courier_partner || "").trim().toLowerCase();
    const r1 = (sub.remarks_1 || "").trim().toLowerCase();
    const r2 = (sub.remarks_2 || "").trim().toLowerCase();
    const pkg = (sub.package_name || "").trim().toLowerCase();
    const pid = String(sub.plan_id || "");
    const allRemarks = `${cp} ${r1} ${r2} ${pkg}`;

    // 1. Direct / Hand Delivery (Client manual: 17)
    if (
      pid === "5" ||
      r1.includes("direct") || r1.includes("by hand") || r1.includes("hand delivery") ||
      r2.includes("direct") || r2.includes("by hand") || r2.includes("hand delivery") ||
      cp.includes("direct") || cp.includes("hand") ||
      pkg.includes("direct")
    ) {
      return "direct";
    }

    // 2. Speed Post (Client manual: 74)
    if (
      pid === "3" ||
      /speed\s*post/i.test(r1) || /speed\s*post/i.test(r2) ||
      cp.includes("speed") ||
      pkg.includes("speed")
    ) {
      return "speed_post";
    }

    // 3. Book Post (Client manual: 30)
    if (
      pid === "4" ||
      ((/book\s*post/i.test(r1) || /book\s*post/i.test(r2) || pkg.includes("book")) &&
       !r1.includes("stop") && !r2.includes("stop"))
    ) {
      return "book_post";
    }

    // 4. Courier Service (Client manual: 201)
    if (
      pid === "2" ||
      r1.includes("courier") ||
      (r2.includes("courier") && !r2.includes("courier upto 2021")) ||
      cp.includes("courier") ||
      pkg.includes("courier") ||
      Boolean(sub.courier_partner && sub.courier_partner.trim())
    ) {
      return "courier";
    }

    // 5. Air Mail / International Post (Client manual: 35)
    // Physical international dispatch: only if destination address is outside India
    const addr = (sub.address || "").toLowerCase();
    const city = (sub.city || "").toLowerCase();
    const state = `${sub.state || ""} ${sub.sub_state || ""} ${sub.user_state || ""}`.toLowerCase();
    const pincode = String(sub.pincode || "").trim();
    const isLocalIndianAddress =
      /tamil nadu|chennai|bangalore|karnataka|mumbai|delhi|hyderabad|600\d{3}|560\d{3}|641\d{3}/i.test(addr) ||
      /tamil nadu|chennai|bangalore|karnataka|mumbai|delhi|hyderabad|madambakkam|alwarpet|velachery|nagar|thiruvanmiyur/i.test(city) ||
      /tamil nadu|karnataka|maharashtra|delhi|andhra|telangana|kerala/i.test(state) ||
      ((sub.user_country || "").toLowerCase() === "india" && /^\d{6}$/.test(pincode));

    const foreignCountry =
      (sub.country && sub.country.toLowerCase() !== "india" && sub.country.trim() !== "") ||
      (sub.sub_country && sub.sub_country.toLowerCase() !== "india" && sub.sub_country.trim() !== "") ||
      (sub.user_country && sub.user_country.toLowerCase() !== "india" && sub.user_country.trim() !== "");

    if (
      (foreignCountry || pid === "6" || Number(sub.is_abroad) === 1 || /(air\s*mail|abroad)/i.test(allRemarks)) &&
      !isLocalIndianAddress
    ) {
      return "air_mail";
    }

    // 6. Default to General Post
    return "general_post";
  }, []);

  const handleCategoryChange = (cat) => {
    setPrintLabelFilter(cat);
    if (["sub_paid_active", "orders_paid", "sub_all"].includes(cat)) {
      if (printDateScope !== "all") {
        setPrintDateScope("all");
      }
    } else if (["daily_combined", "daily_subs", "daily_orders"].includes(cat)) {
      if (printDateScope === "all") {
        setPrintDateScope("today");
      }
    }
  };

  const getFilteredPrintRecords = useCallback(() => {
    const includeSubs = (
      printLabelFilter === "daily_combined" ||
      printLabelFilter === "daily_subs" ||
      printLabelFilter === "sub_paid_active" ||
      printLabelFilter === "sub_all"
    );

    const includeOrders = (
      printLabelFilter === "daily_combined" ||
      printLabelFilter === "daily_orders" ||
      printLabelFilter === "orders_paid"
    );

    let matchedSubs = [];
    let subsForDispatch = [];
    if (includeSubs) {
      let list = subscribersCache || [];
      if (printLabelFilter !== "sub_all") {
        list = list.filter((sub) => sub.status === "paid" && Number(sub.is_active) === 1);
      } else {
        // Even in "all" mode, exclude paused/cancelled subscribers from shipping labels
        list = list.filter((sub) => sub.status !== "paused" && sub.status !== "cancelled");
      }
      if (printDateScope !== "all") {
        list = list.filter(isSubscriberInDateScope);
      }
      subsForDispatch = list;
      if (printDispatchFilter && printDispatchFilter !== "all") {
        list = list.filter((sub) => getSubscriberDispatchMode(sub) === printDispatchFilter);
      }
      matchedSubs = list;
    }

    let matchedOrders = [];
    if (includeOrders) {
      let list = (orders || []).filter(isOrderDispatchable);
      if (printDateScope !== "all") {
        list = list.filter(isOrderInDateScope);
      }
      if (printDispatchFilter && printDispatchFilter !== "all") {
        list = list.filter((o) => getOrderDispatchMode(o) === printDispatchFilter);
      }
      matchedOrders = list;
    }

    const dispatchCounts = {
      all: subsForDispatch.length,
      general_post: 0,
      speed_post: 0,
      book_post: 0,
      courier: 0,
      direct: 0,
      air_mail: 0,
    };
    subsForDispatch.forEach((sub) => {
      const mode = getSubscriberDispatchMode(sub);
      if (dispatchCounts[mode] !== undefined) {
        dispatchCounts[mode] += 1;
      }
    });

    const totalCount = matchedSubs.length + matchedOrders.length;
    const totalSheets = Math.ceil(totalCount / 21);
    const renewalsCount = matchedSubs.filter((s) => Number(s.is_renewal) === 1).length;
    const newSubsCount = matchedSubs.length - renewalsCount;

    return {
      includeSubs,
      includeOrders,
      matchedSubs,
      matchedOrders,
      subsCount: matchedSubs.length,
      renewalsCount,
      newSubsCount,
      ordersCount: matchedOrders.length,
      totalCount,
      totalSheets,
      dispatchCounts
    };
  }, [
    subscribersCache,
    orders,
    printLabelFilter,
    printDateScope,
    printDispatchFilter,
    isSubscriberInDateScope,
    isOrderInDateScope,
    getSubscriberDispatchMode
  ]);

  const labelPreview = useMemo(() => {
    return getFilteredPrintRecords();
  }, [getFilteredPrintRecords]);

  // Combined list of filtered items (subscribers + orders)
  const allFilteredItems = useMemo(() => {
    if (printDateScope === "all") return [];
    const subs = (labelPreview.matchedSubs || []).map((s) => ({
      key: `sub-${s.id}`,
      type: "sub",
      id: s.id,
      login_id: s.login_id || `ID: ${s.id}`,
      name: s.user_name || "Unnamed Subscriber",
      phone: s.user_phone || "",
      city: s.city || "",
      state: s.state || "",
      pincode: s.pincode || "",
      is_renewal: Number(s.is_renewal) === 1,
      dispatch_mode: getSubscriberDispatchMode(s),
      raw: s
    }));

    const ords = (labelPreview.matchedOrders || []).map((o) => ({
      key: `order-${o.id}`,
      type: "order",
      id: o.id,
      login_id: `ORD-${o.id}`,
      name: o.name || "Unnamed Customer",
      phone: o.phone || "",
      city: o.city || "",
      state: o.state || "",
      pincode: o.pincode || "",
      is_renewal: false,
      dispatch_mode: getOrderDispatchMode(o),
      raw: o
    }));

    return [...subs, ...ords];
  }, [printDateScope, labelPreview.matchedSubs, labelPreview.matchedOrders, getSubscriberDispatchMode]);

  // Synchronize selected keys when filter criteria or records change
  useEffect(() => {
    if (!showPrintLabelModal) return;
    const { matchedSubs, matchedOrders } = getFilteredPrintRecords();
    const allKeys = [
      ...matchedSubs.map((s) => `sub-${s.id}`),
      ...matchedOrders.map((o) => `order-${o.id}`)
    ];
    setSelectedMemberKeys(allKeys);
    setMemberSearchQuery("");
  }, [
    showPrintLabelModal,
    printDateScope,
    printTargetDate,
    printFromDate,
    printToDate,
    printLabelFilter,
    printDispatchFilter,
    subscribersCache,
    orders,
    getFilteredPrintRecords
  ]);

  // Search filter for checklist
  const displayedItems = useMemo(() => {
    if (!memberSearchQuery.trim()) return allFilteredItems;
    const q = memberSearchQuery.trim().toLowerCase();
    return allFilteredItems.filter((item) =>
      (item.login_id && item.login_id.toLowerCase().includes(q)) ||
      (item.name && item.name.toLowerCase().includes(q)) ||
      (item.city && item.city.toLowerCase().includes(q)) ||
      (item.pincode && String(item.pincode).includes(q)) ||
      (item.phone && String(item.phone).includes(q))
    );
  }, [allFilteredItems, memberSearchQuery]);

  // Selection toggle handlers
  const toggleSelectAll = (selectAll) => {
    if (selectAll) {
      if (memberSearchQuery.trim()) {
        const set = new Set(selectedMemberKeys);
        displayedItems.forEach((item) => set.add(item.key));
        setSelectedMemberKeys(Array.from(set));
      } else {
        setSelectedMemberKeys(allFilteredItems.map((item) => item.key));
      }
    } else {
      if (memberSearchQuery.trim()) {
        const displayedSet = new Set(displayedItems.map((item) => item.key));
        setSelectedMemberKeys(selectedMemberKeys.filter((k) => !displayedSet.has(k)));
      } else {
        setSelectedMemberKeys([]);
      }
    }
  };

  const toggleMemberKey = (key) => {
    setSelectedMemberKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const selectedCount = printDateScope === "all" ? labelPreview.totalCount : selectedMemberKeys.length;
  const selectedSheets = printDateScope === "all" ? labelPreview.totalSheets : Math.ceil(selectedCount / 21);

  // Pre-fetch subscribers on component mount and refresh whenever print label modal is opened
  useEffect(() => {
    if (showPrintLabelModal && !loadingSubscribersCache) {
      setLoadingSubscribersCache(true);
      fetch(`${SUBSCRIBERS_API}?skip_sync=true`)
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success") {
            setSubscribersCache(data.subscriptions || []);
          }
        })
        .catch((err) => console.error("Error pre-fetching subscribers:", err))
        .finally(() => setLoadingSubscribersCache(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPrintLabelModal]);

  // Initial load
  useEffect(() => {
    if (subscribersCache.length === 0 && !loadingSubscribersCache) {
      setLoadingSubscribersCache(true);
      fetch(`${SUBSCRIBERS_API}?skip_sync=true`)
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success") {
            setSubscribersCache(data.subscriptions || []);
          }
        })
        .catch((err) => console.error("Error initial fetching subscribers:", err))
        .finally(() => setLoadingSubscribersCache(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseOrderItems = (order) => {
    if (!order) return [];
    if (Array.isArray(order.items)) return order.items;
    if (typeof order.items === 'string') {
      try {
        const parsed = JSON.parse(order.items);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {}
    }
    return [];
  };

  const convertNumberToWords = (amount) => {
    const num = Math.floor(Number(amount));
    if (!num || isNaN(num)) return "Rupees Zero Only";
    
    const single = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const double = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const formatHundreds = (n) => {
      let str = "";
      if (n > 99) {
        str += single[Math.floor(n / 100)] + " Hundred ";
        n %= 100;
      }
      if (n > 19) {
        str += double[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + single[n % 10] : "");
      } else if (n > 0) {
        str += single[n];
      }
      return str.trim();
    };

    let wordStr = "";
    let rem = num;
    if (Math.floor(rem / 10000000) > 0) {
      wordStr += formatHundreds(Math.floor(rem / 10000000)) + " Crore ";
      rem %= 10000000;
    }
    if (Math.floor(rem / 100000) > 0) {
      wordStr += formatHundreds(Math.floor(rem / 100000)) + " Lakh ";
      rem %= 100000;
    }
    if (Math.floor(rem / 1000) > 0) {
      wordStr += formatHundreds(Math.floor(rem / 1000)) + " Thousand ";
      rem %= 1000;
    }
    if (rem > 0) {
      wordStr += formatHundreds(rem);
    }

    return "Indian Rupees " + wordStr.trim() + " Only";
  };


  const ordersPerPage = 8;

  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => {
      setToast(null);
    }, 3500);
  }, []);

  /* ================= FETCH ORDERS ================= */
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${ORDERS_API}?action=list`
      );
      const data = await res.json();
      setOrders(data?.orders || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  /* ================= PRINT HELPER (HIDDEN IFRAME - IMMUNE TO POPUP BLOCKERS) ================= */
  const printHTML = (htmlContent) => {
    let iframe = document.getElementById("admin-print-frame");
    if (iframe) {
      try {
        document.body.removeChild(iframe);
      } catch (e) {}
    }
    iframe = document.createElement("iframe");
    iframe.id = "admin-print-frame";
    iframe.style.position = "fixed";
    iframe.style.left = "-10000px";
    iframe.style.top = "-10000px";
    iframe.style.width = "20.9cm";
    iframe.style.height = "29.81cm";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(htmlContent);
    doc.close();

    setTimeout(() => {
      if (iframe.contentWindow) {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }
    }, 800);
  };

  /* ================= PRINT REPORT ================= */
  const printOrders = () => {
    const table = document.getElementById("orders-table-print");
    if (!table) return;

    const htmlContent = `
      <html>
        <head>
          <title>Orders Report</title>
          <style>
            body{
              font-family: Arial;
              padding:20px;
            }
            h2{
              text-align:center;
              margin-bottom:20px;
            }
            table{
              width:100%;
              border-collapse:collapse;
            }
            th, td{
              border:1px solid #ddd;
              padding:8px;
              text-align:left;
            }
            th{
              background:#f4f4f4;
            }
            .actions, th:last-child, td:last-child {
              display: none !important;
            }
            .payment-select {
              display: none !important;
            }
          </style>
        </head>
        <body>
          <h2>Orders Report</h2>
          ${table.innerHTML}
        </body>
      </html>
    `;

    printHTML(htmlContent);
  };



  /* ================= PRINT SHIPPING LABELS (SUBSCRIBERS & BOOK ORDERS) ================= */
  const handlePrintLabels = async () => {
    setPrintingLabels(true);
    try {
      // Ensure subscribers are loaded if required
      if (labelPreview.includeSubs && (!subscribersCache || subscribersCache.length === 0)) {
        const res = await fetch(`${SUBSCRIBERS_API}?skip_sync=true`);
        const data = await res.json();
        if (data.status === "success") {
          setSubscribersCache(data.subscriptions || []);
        } else {
          showToast(data.message || "Failed to fetch subscribers", "error");
          setPrintingLabels(false);
          return;
        }
      }

      // Re-run filtered print records to ensure exact sync
      const { matchedSubs, matchedOrders, totalCount } = getFilteredPrintRecords();

      if (totalCount === 0) {
        showToast("No records found matching the selected date and filter.", "error");
        setPrintingLabels(false);
        return;
      }

      // Filter by user selection (in "All Records (Monthly)", print all matched records directly)
      const isMonthlyAll = printDateScope === "all";
      const activeKeys = new Set(selectedMemberKeys);
      const selectedSubs = isMonthlyAll ? matchedSubs : matchedSubs.filter((sub) => activeKeys.has(`sub-${sub.id}`));
      const selectedOrders = isMonthlyAll ? matchedOrders : matchedOrders.filter((o) => activeKeys.has(`order-${o.id}`));

      if (selectedSubs.length === 0 && selectedOrders.length === 0) {
        showToast("No records found matching the selected filter to print labels.", "error");
        setPrintingLabels(false);
        return;
      }

      const subItems = selectedSubs.map((sub) => {
        const combinedRemarks = [
          sub.remarks_1,
          sub.remarks_2,
          sub.package_name,
          sub.payment_mode,
          sub.courier_partner
        ].filter(Boolean).join(" ");

        const isCourierOrSpecialPost = /(book\s*post|bookpost|speed\s*post|speedpost|courier|register(ed)?\s*post|reg\s*\.?\s*post|parcel|express)/i.test(combinedRemarks);

        return {
          id_display: sub.login_id || `ID: ${sub.id}`,
          name: sub.user_name || "",
          address: sub.address || "",
          city: sub.city || "",
          state: sub.state,
          pincode: sub.pincode || "",
          phone: sub.user_phone || "",
          landline: sub.landline || "",
          showPhone: isCourierOrSpecialPost,
          badge: ""
        };
      });

      const orderItems = selectedOrders.map((o) => {
        return {
          id_display: `ORD-${o.id}`,
          name: o.name || "",
          address: o.address || "",
          city: o.city || "",
          state: o.state,
          pincode: o.pincode || "",
          phone: o.phone || "",
          landline: "",
          showPhone: true,
          badge: ""
        };
      });

      const labelItems = [...subItems, ...orderItems];

      if (labelItems.length === 0) {
        showToast("No records found matching the selected date and filter.", "error");
        return;
      }

      // Sort labels by Indian Pincode in ascending order (e.g. 110003 New Delhi -> 741313 West Bengal)
      // to match India Post dispatch bundling and postal sorting regulations
      const extractNumericPincode = (item) => {
        if (!item) return 999999999;
        const rawPin = item.pincode ? String(item.pincode).trim().replace(/\D/g, "") : "";
        if (rawPin.length === 6) {
          const num = parseInt(rawPin, 10);
          if (num >= 100000 && num <= 999999) return num;
        }
        if (item.address) {
          const m = String(item.address).match(/\b([1-9][0-9]{5})\b/);
          if (m) {
            return parseInt(m[1], 10);
          }
        }
        // Non-6-digit PIN (e.g. abroad or unlisted) placed at the end
        return 9000000 + (rawPin.length > 0 ? parseInt(rawPin.substring(0, 6), 10) : 999999);
      };

      const extractNumericId = (idDisplay) => {
        if (!idDisplay) return 9999999;
        const compMatch = String(idDisplay).match(/^C-(\d+)/i);
        if (compMatch) return parseInt(compMatch[1], 10);
        const numMatch = String(idDisplay).match(/\d+/);
        return numMatch ? parseInt(numMatch[0], 10) : 9999999;
      };

      labelItems.sort((a, b) => {
        const pinA = extractNumericPincode(a);
        const pinB = extractNumericPincode(b);
        if (pinA !== pinB) {
          return pinA - pinB;
        }
        const idA = extractNumericId(a.id_display);
        const idB = extractNumericId(b.id_display);
        if (idA !== idB) {
          return idA - idB;
        }
        return (a.name || "").localeCompare(b.name || "");
      });
      
      // Pad to multiples of 21 (3 columns x 7 rows)
      const itemsPerPage = 21;
      const totalLabelsNeeded = Math.ceil(labelItems.length / itemsPerPage) * itemsPerPage;
      const paddedItems = [...labelItems];
      while (paddedItems.length < totalLabelsNeeded) {
        paddedItems.push(null);
      }
      
      // Group into pages
      const pages = [];
      for (let i = 0; i < paddedItems.length; i += itemsPerPage) {
        pages.push(paddedItems.slice(i, i + itemsPerPage));
      }

      // Apply batching / range filtering for fast browser rendering
      let targetPages = pages;
      if (printBatchLimit === "10") {
        targetPages = pages.slice(0, 10);
      } else if (printBatchLimit === "20") {
        targetPages = pages.slice(0, 20);
      } else if (printBatchLimit === "50") {
        targetPages = pages.slice(0, 50);
      } else if (printBatchLimit === "custom") {
        const start = Math.max(0, (parseInt(customStartPage, 10) || 1) - 1);
        const end = Math.min(pages.length, parseInt(customEndPage, 10) || pages.length);
        targetPages = pages.slice(start, end);
      }

      const getDispatchTitleStr = (key) => {
        if (key === "speed_post") return "Speed Post";
        if (key === "book_post") return "Book Post";
        if (key === "courier") return "Courier";
        if (key === "direct") return "Direct Delivery";
        if (key === "air_mail") return "Air Mail";
        if (key === "general_post") return "General Post";
        return "";
      };
      const dispatchTitleStr = printDispatchFilter !== "all" ? ` (${getDispatchTitleStr(printDispatchFilter)})` : "";

      let htmlContent = `
        <html>
          <head>
            <title>Shipping Labels - ${
              printLabelFilter === "daily_combined"
                ? "Daily Combined Dispatch"
                : (printLabelFilter === "daily_orders" || printLabelFilter === "orders_paid")
                ? "Book Store Orders"
                : `Subscribers${dispatchTitleStr}`
            }</title>
            <style>
              @page {
                size: 20.9cm 29.81cm;
                margin: 0 !important;
              }
              * {
                box-sizing: border-box !important;
                margin: 0 !important;
                padding: 0 !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                width: 20.9cm !important;
                height: 29.81cm !important;
                background-color: #f0f0f0;
                font-family: Arial, sans-serif;
              }
              @media print {
                @page {
                  size: 20.9cm 29.81cm;
                  margin: 0 !important;
                }
                html, body {
                  margin: 0 !important;
                  padding: 0 !important;
                  width: 20.9cm !important;
                  height: 29.81cm !important;
                  background: white !important;
                }
                .sheet {
                  box-shadow: none !important;
                  margin: 0 !important;
                }
                .label-box, .empty-label {
                  border: none !important;
                }
              }
              .sheet {
                width: 20.9cm !important;
                height: 29.81cm !important;
                max-height: 29.81cm !important;
                min-height: 29.81cm !important;
                box-sizing: border-box !important;
                padding-top: ${labelTopMargin}cm !important;
                padding-right: 0.7cm !important;
                padding-bottom: 0.85cm !important;
                padding-left: ${labelLeftMargin}cm !important;
                margin: 0 auto !important;
                background: white !important;
                overflow: hidden !important;
                display: grid !important;
                grid-template-columns: 6.3cm 6.3cm 6.3cm !important;
                grid-template-rows: repeat(7, 3.8cm) !important;
                column-gap: 0.25cm !important;
                row-gap: 0cm !important;
                justify-content: start !important;
                align-content: start !important;
                page-break-after: always !important;
                break-after: page !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              .sheet:last-child, .sheet:last-of-type {
                page-break-after: auto !important;
                break-after: auto !important;
              }
              .label-box {
                width: 6.3cm !important;
                height: 3.8cm !important;
                max-width: 6.3cm !important;
                min-width: 6.3cm !important;
                max-height: 3.8cm !important;
                min-height: 3.8cm !important;
                margin: 0 !important;
                border: none !important;
                padding-top: 0.2cm !important;
                padding-right: 0.3cm !important;
                padding-bottom: 0.1cm !important;
                padding-left: 0.3cm !important;
                font-family: Calibri, 'Segoe UI', Arial, sans-serif !important;
                box-sizing: border-box !important;
                display: flex !important;
                flex-direction: column !important;
                justify-content: flex-start !important;
                text-align: left !important;
                color: #000 !important;
                position: relative !important;
                overflow: hidden !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              .label-box *, .label-box div, .label-box span, .label-box p, .label-box h1, .label-box h2, .label-box h3 {
                margin: 0 !important;
                padding: 0 !important;
                box-sizing: border-box !important;
              }
              .label-box .label-header {
                display: flex !important;
                justify-content: space-between !important;
                align-items: center !important;
                font-weight: 700 !important;
                border-bottom: 1px solid #b0b0b0 !important;
                margin-top: 0 !important;
                padding-top: 0 !important;
                padding-bottom: 1px !important;
                margin-bottom: 1px !important;
                font-size: 8.5pt !important;
                line-height: 1.15 !important;
                color: #000 !important;
                width: 100% !important;
                box-sizing: border-box !important;
                flex-shrink: 0 !important;
              }
              .label-box .label-header span {
                font-size: 9pt !important;
                font-weight: 800 !important;
                color: #000 !important;
                white-space: nowrap !important;
              }
              .label-box .label-name {
                font-weight: 700 !important;
                margin-top: 1px !important;
                margin-bottom: 1px !important;
                font-size: 9.5pt !important;
                line-height: 1.15 !important;
                color: #000 !important;
                white-space: nowrap !important;
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                flex-shrink: 0 !important;
              }
              .label-box .label-address {
                font-size: 9.5pt !important;
                font-weight: 700 !important;
                line-height: 1.15 !important;
                word-wrap: break-word !important;
                overflow: hidden !important;
                margin-top: 1px !important;
                margin-bottom: 1px !important;
                color: #000 !important;
                flex: 0 1 auto !important;
                min-height: 0 !important;
              }
              .label-box .label-address.compact-address {
                font-size: 8pt !important;
                font-weight: 700 !important;
                line-height: 1.12 !important;
              }
              .label-box .label-location {
                display: flex !important;
                justify-content: space-between !important;
                align-items: flex-start !important;
                font-weight: 700 !important;
                margin-top: 1px !important;
                margin-bottom: 0 !important;
                border-top: 1px dashed #b0b0b0 !important;
                padding-top: 1px !important;
                color: #000 !important;
                width: 100% !important;
                overflow: hidden !important;
                flex-shrink: 0 !important;
              }
              .label-box .label-location .location-city {
                font-size: 9pt !important;
                white-space: normal !important;
                word-wrap: break-word !important;
                overflow: hidden !important;
                flex: 1 1 auto !important;
                min-width: 0 !important;
                padding-right: 4px !important;
                color: #000 !important;
                line-height: 1.15 !important;
              }
              .label-box .label-location .location-pin {
                font-size: 9.5pt !important;
                font-weight: 800 !important;
                white-space: nowrap !important;
                flex: 0 0 auto !important;
                color: #000 !important;
                letter-spacing: 0.5px !important;
                padding-top: 1px !important;
              }
              .label-box .label-footer {
                display: flex !important;
                justify-content: space-between !important;
                align-items: center !important;
                font-size: 7.5pt !important;
                line-height: 1.1 !important;
                color: #333 !important;
                margin-top: 1px !important;
                width: 100% !important;
                overflow: hidden !important;
                flex-shrink: 0 !important;
              }
              .label-box .label-badge {
                font-size: 7pt !important;
                font-weight: 600 !important;
                color: #701e24 !important;
                background: #fdf2f2 !important;
                padding: 1px 3px !important;
                border-radius: 3px !important;
                border: 1px solid #fed7d7 !important;
                white-space: nowrap !important;
                max-width: 100% !important;
                overflow: hidden !important;
                text-overflow: ellipsis !important;
              }
              .empty-label {
                background: transparent !important;
                border: none !important;
              }
            </style>
          </head>
          <body>
      `;
      
      const INDIAN_STATES_LIST = [
        "tamil nadu", "tamilnadu", "kerala", "karnataka", "andhra pradesh", "telangana",
        "maharashtra", "gujarat", "rajasthan", "uttar pradesh", "madhya pradesh",
        "west bengal", "bihar", "odisha", "punjab", "haryana", "delhi",
        "assam", "goa", "jharkhand", "chhattisgarh", "uttarakhand", "himachal pradesh",
        "jammu & kashmir", "tripura", "meghalaya", "manipur", "nagaland", "mizoram",
        "arunachal pradesh", "sikkim", "puducherry", "chandigarh", "ladakh"
      ];

      const PIN_TO_CITY_MAP = {
        '600': 'Chennai', '601': 'Tiruvallur', '602': 'Tiruvallur', '603': 'Kanchipuram',
        '604': 'Tindivanam', '605': 'Puducherry / Villupuram', '606': 'Tiruvannamalai',
        '607': 'Cuddalore', '608': 'Chidambaram', '609': 'Mayiladuthurai',
        '610': 'Tiruvarur', '611': 'Nagapattinam', '612': 'Kumbakonam', '613': 'Thanjavur',
        '614': 'Pattukkottai', '620': 'Trichy', '621': 'Trichy', '622': 'Pudukkottai',
        '624': 'Dindigul', '625': 'Madurai', '626': 'Virudhunagar', '627': 'Tirunelveli',
        '628': 'Tuticorin', '629': 'Kanyakumari', '630': 'Karaikudi / Sivagangai',
        '631': 'Kanchipuram', '632': 'Vellore', '635': 'Hosur / Krishnagiri',
        '636': 'Salem', '637': 'Namakkal', '638': 'Erode', '639': 'Karur',
        '641': 'Coimbatore', '642': 'Pollachi', '643': 'Ooty / Nilgiris',
        '560': 'Bangalore', '561': 'Bangalore Rural', '562': 'Bangalore Rural',
        '570': 'Mysore', '400': 'Mumbai', '411': 'Pune', '110': 'New Delhi',
        '500': 'Hyderabad', '517': 'Tirupati', '520': 'Vijayawada',
        '530': 'Visakhapatnam', '201': 'Noida / Ghaziabad'
      };

      const resolveCityForLabel = (city, state, address, pincode) => {
        let c = (city || "").trim().replace(/,\s*$/, "");
        let s = (state || "").trim().replace(/,\s*$/, "");
        let pin = String(pincode || "").trim().replace(/\D/g, "");
        const isIndianPin = pin.length === 6;

        // Strip trailing state names like ', Tamil Nadu' if combined into city
        if (c) {
          c = c.replace(/,\s*(Tamil\s*Nadu|Andhra\s*Pradesh|Telangana|Karnataka|Kerala|Maharashtra|Uttar\s*Pradesh|Madhya\s*Pradesh|Gujarat|Rajasthan|West\s*Bengal|Bihar|Jharkhand|Odisha|Punjab|Haryana|Delhi|Assam|Goa|Puducherry)\s*$/i, "").trim().replace(/,\s*$/, "");
        }

        // 1. If city exists and is NOT a generic state name, use it directly
        if (c && !INDIAN_STATES_LIST.includes(c.toLowerCase())) {
          return c;
        }

        // 2. In the members Excel sheet, many cities were mistakenly entered in the "State" column (e.g. Chennai, Coimbatore, Noida, Trichy)
        // If state exists and is NOT an actual state name, it IS the city!
        if (s && !INDIAN_STATES_LIST.includes(s.toLowerCase())) {
          return s;
        }

        // 3. If city was empty or only had a generic state name, resolve from 6-digit Indian PIN
        if (isIndianPin) {
          const p3 = pin.substring(0, 3);
          if (PIN_TO_CITY_MAP[p3]) {
            return PIN_TO_CITY_MAP[p3];
          }
        }

        // 4. Fallback
        return c || s || "";
      };

      const escapeHtml = (val) => {
        if (!val) return "";
        return String(val)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      };

      const formatAddressHtml = (addr) => {
        if (!addr) return "";
        return escapeHtml(addr).replace(/\r?\n/g, "<br/>");
      };

      targetPages.forEach((page) => {
        htmlContent += `<div class="sheet">`;
        page.forEach((item) => {
          if (item) {
            const rawPin = String(item.pincode || "").trim();
            const pinDigits = rawPin.replace(/\D/g, "");
            const isIndianPin = pinDigits.length === 6;
            const displayPin = isIndianPin ? pinDigits : rawPin;

            const cityPart = resolveCityForLabel(item.city, item.state, item.address, item.pincode);

            const phoneStr = (item.showPhone && item.phone) ? `Ph: ${item.phone}${item.landline ? ' / ' + item.landline : ''}` : '';
            const isLongAddr = Boolean(
              (item.address && item.address.length > 85) || 
              (item.address && item.address.split(/\r?\n/).length >= 4)
            );
            const hasFooter = Boolean(phoneStr || item.badge);

            htmlContent += `
              <div class="label-box">
                <div class="label-header">
                  <span>${escapeHtml(item.id_display)}</span>
                </div>
                <div class="label-name">${escapeHtml(item.name)}</div>
                <div class="label-address ${isLongAddr ? 'compact-address' : ''}">
                  ${formatAddressHtml(item.address)}
                </div>
                <div class="label-location">
                  <span class="location-city">${escapeHtml(cityPart)}</span>
                  ${displayPin ? `<span class="location-pin">${cityPart ? '- ' : ''}${escapeHtml(displayPin)}</span>` : ''}
                </div>
                ${hasFooter ? `
                  <div class="label-footer">
                    <span>${escapeHtml(phoneStr)}</span>
                    ${item.badge ? `<span class="label-badge">${escapeHtml(item.badge)}</span>` : ''}
                  </div>
                ` : ''}
              </div>
            `;
          } else {
            htmlContent += `<div class="label-box empty-label"></div>`;
          }
        });
        htmlContent += `</div>`;
      });
      
      htmlContent += `
          </body>
        </html>
      `;
      
      printHTML(htmlContent);
      setShowPrintLabelModal(false);
    } catch (err) {
      console.error(err);
      showToast("Error generating labels: " + err.message, "error");
    } finally {
      setPrintingLabels(false);
    }
  };

  /* ================= UPDATE POD NUMBER ================= */
  const updatePodNumber = async (id, pod_number) => {
    setActionLoading(id);
    setOrders((prev) =>
      prev.map((o) =>
        o.id === id
          ? {
              ...o,
              pod_number,
            }
          : o
      )
    );

    try {
      const res = await fetch(
        `${ORDERS_API}?action=update_pod_number`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id,
            pod_number,
          }),
        }
      );
      const data = await res.json();
      if (data?.status === "success") {
        showToast("POD tracking number updated successfully!", "success");
      } else {
        showToast(data?.message || "Failed to update POD tracking number", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Error updating POD tracking number", "error");
    } finally {
      setActionLoading(null);
    }
  };

  /* ================= UPDATE ORDER STATUS ================= */
  const updateStatus = async (id, status) => {
    setActionLoading(id);
    setOrders((prev) =>
      prev.map((o) =>
        o.id === id
          ? {
              ...o,
              order_status: status,
            }
          : o
      )
    );

    try {
      await fetch(
        `${ORDERS_API}?action=update_status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id,
            status,
          }),
        }
      );
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  /* ================= UPDATE PAYMENT STATUS ================= */
  const updatePaymentStatus = async (
    id,
    payment_status
  ) => {
    setActionLoading(id);
    setOrders((prev) =>
      prev.map((o) =>
        o.id === id
          ? {
              ...o,
              payment_status,
            }
          : o
      )
    );

    try {
      await fetch(
        `${ORDERS_API}?action=update_payment_status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id,
            payment_status,
          }),
        }
      );
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  /* ================= DELETE ORDER ================= */
  const deleteOrder = async (id) => {
    const ok = await showConfirm({
      title: "Delete Order",
      message: "Are you sure you want to delete this order?",
      subMessage: "This action cannot be undone and will remove the order record.",
      confirmText: "Delete Order",
      type: "danger"
    });
    if (!ok) return;

    setActionLoading(id);
    setOrders((prev) =>
      prev.filter((o) => o.id !== id)
    );

    try {
      await fetch(
        `${ORDERS_API}?action=delete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id }),
        }
      );
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  /* ================= STATUS CLASS ================= */
  const getStatusClass = (status = "") => {
    return `status-pill ${status}`;
  };

  const getPaymentStatusClass = (status = "") => {
    if (status === "success") {
      return "status-pill success";
    }
    if (status === "failed") {
      return "status-pill failed";
    }
    return "status-pill pending";
  };

  /* ================= FILTER ================= */
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const s = search.toLowerCase();

      const matchSearch =
        order.id?.toString().includes(s) ||
        order.email?.toLowerCase().includes(s) ||
        order.name?.toLowerCase().includes(s) ||
        order.phone?.toLowerCase().includes(s) ||
        order.payment_id?.toLowerCase().includes(s);

      const matchStatus =
        statusFilter === "all" ||
        order.order_status === statusFilter;

      const isPaid = order.payment_status === "success" || order.payment_status === "paid";
      const matchPayment =
        paymentFilter === "all" ||
        (paymentFilter === "paid_only" && isPaid) ||
        (paymentFilter === "pending" && !isPaid);

      return (
        matchSearch &&
        matchStatus &&
        matchPayment
      );
    });
  }, [orders, search, statusFilter, paymentFilter]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredOrders.length /
      ordersPerPage
    )
  );

  const currentOrders =
    filteredOrders.slice(
      (currentPage - 1) *
        ordersPerPage,
      currentPage *
        ordersPerPage
    );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const goToPage = (page) => {
    if (
      page >= 1 &&
      page <= totalPages
    ) {
      setCurrentPage(page);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="admin-container orders-admin-container">
      {/* TOAST NOTIFICATION */}
      {toast && (
        <div className={`orders-toast ${toast.type}`}>
          <i
            className={`bi ${
              toast.type === "success"
                ? "bi-check-circle-fill"
                : "bi-exclamation-circle-fill"
            }`}
          ></i>
          <span>{toast.msg}</span>
        </div>
      )}

      {/* ================= HEADER ================= */}
      <div className="orders-dashboard-header">
        <div className="header-title-area">
          <h1>
            <i className="bi bi-cart3"></i> Orders & Fulfillment Center
          </h1>
          <p>Monitor, search, and manage customer and subscriber orders</p>
        </div>

        <div className="orders-header-actions">
          <button
            onClick={printOrders}
            className="action-btn-outline"
          >
            <i className="bi bi-printer-fill"></i> Print Report
          </button>
          <button
            onClick={() => setShowPrintLabelModal(true)}
            className="action-btn-solid"
          >
            <i className="bi bi-tags-fill"></i> Print Shipping Labels
          </button>
        </div>
      </div>

      {/* ================= STATS ================= */}
      <div className="orders-metrics-grid">
        <div className="metric-card">
          <div className="metric-icon total"><i className="bi bi-bag-check-fill"></i></div>
          <div className="metric-info">
            <span className="metric-value">
              {orders.filter((o) => o.payment_status === "success" || o.payment_status === "paid").length}
            </span>
            <span className="metric-label">Paid Orders</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon pending"><i className="bi bi-clock-history"></i></div>
          <div className="metric-info">
            <span className="metric-value">
              {orders.filter((o) => (o.payment_status === "success" || o.payment_status === "paid") && o.order_status === "pending").length}
            </span>
            <span className="metric-label">To Fulfill (Pending)</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon delivered"><i className="bi bi-check2-circle"></i></div>
          <div className="metric-info">
            <span className="metric-value">
              {orders.filter((o) => (o.payment_status === "success" || o.payment_status === "paid") && o.order_status === "delivered").length}
            </span>
            <span className="metric-label">Delivered</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon revenue"><i className="bi bi-currency-rupee"></i></div>
          <div className="metric-info">
            <span className="metric-value">
              ₹{orders
                .filter((o) => o.payment_status === "success" || o.payment_status === "paid")
                .reduce((sum, o) => sum + Number(o.total_amount || 0), 0)
                .toLocaleString("en-IN")}
            </span>
            <span className="metric-label">Total Verified Revenue</span>
          </div>
        </div>
      </div>

      {/* ================= FILTERS ================= */}
      <div className="orders-filter-deck">
        <div className="search-box-wrapper">
          <i className="bi bi-search"></i>
          <input
            type="text"
            className="admin-search-input"
            style={{ paddingLeft: "48px" }}
            placeholder="Search by ID, name, email, phone, or payment ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-select-wrapper">
          <i className="bi bi-credit-card-2-front-fill"></i>
          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
          >
            <option value="paid_only">✅ Paid Orders Only (Completed)</option>
            <option value="all">All Orders (including Unpaid)</option>
            <option value="pending">⏳ Unpaid / Abandoned Only</option>
          </select>
        </div>

        <div className="filter-select-wrapper">
          <i className="bi bi-funnel-fill"></i>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Fulfillment Statuses</option>
            <option value="pending">Pending (Unfulfilled)</option>
            <option value="processing">Processing</option>
            <option value="shipping">Shipping</option>
            <option value="delivered">Delivered</option>
            <option value="cancel">Cancelled</option>
          </select>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      {loading ? (
        <div className="orders-loading-box">
          <div className="spinner"></div>
          <p>Syncing live orders database...</p>
        </div>
      ) : (
        <section className="orders-table-section">
          <div className="orders-table-wrapper" id="orders-table-print">
            <table className="orders-admin-table">
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>ID</th>
                  <th style={{ width: "135px" }}>Date</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Total Amount</th>
                  <th>Order Status</th>
                  <th>Payment Status</th>
                  <th>Method</th>
                  <th style={{ width: "230px", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentOrders.length > 0 ? (
                  currentOrders.map((order) => (
                    <React.Fragment key={order.id}>
                      {/* ================= MAIN ROW ================= */}
                      <tr className={expandedOrder === order.id ? "row-active-parent" : ""}>
                        <td className="order-id-td">
                          <strong>#{order.id}</strong>
                        </td>

                        <td className="order-date-td" style={{ whiteSpace: "nowrap" }}>
                          {(() => {
                            const rawDate = order.created_at || order.order_date || order.date;
                            if (!rawDate) return <span style={{ color: "#94a3b8" }}>—</span>;
                            let str = String(rawDate).trim();
                            if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(str) && !str.endsWith("Z") && !/[+-]\d{2}(:\d{2})?$/.test(str)) {
                              str = str.replace(" ", "T") + "Z";
                            }
                            const d = new Date(str);
                            if (isNaN(d.getTime())) return <span>{String(rawDate)}</span>;
                            return (
                              <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                                <strong style={{ fontWeight: 700, color: "#1e293b", fontSize: "13px" }}>
                                  {d.toLocaleDateString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    timeZone: "Asia/Kolkata"
                                  })}
                                </strong>
                                <span style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                                  {d.toLocaleTimeString("en-IN", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    timeZone: "Asia/Kolkata"
                                  })}
                                </span>
                              </div>
                            );
                          })()}
                        </td>

                        <td className="order-customer-td">
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div className="order-avatar" style={{
                              width: "36px",
                              height: "36px",
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, #dfb76c 0%, #b8860b 100%)",
                              color: "#4a1216",
                              fontSize: "13px",
                              fontWeight: "750",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "2px solid #ffffff",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                              textTransform: "uppercase"
                            }}>
                              {(order.name || "C").charAt(0)}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                              <strong style={{ fontWeight: 750, color: "#1e293b" }}>{order.name}</strong>
                              <span className="customer-email-sub" style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px" }}>
                                {order.email}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>{order.phone}</td>

                        <td className="order-total-td">
                          <span style={{ fontWeight: 800, color: "#bc9341", fontSize: "15px" }}>₹{Number(order.total_amount || 0).toLocaleString("en-IN")}</span>
                        </td>

                        {/* ORDER STATUS */}
                        <td>
                          <span className={getStatusClass(order.order_status)}>
                            {order.order_status === "pending" && <i className="bi bi-clock-history"></i>}
                            {order.order_status === "processing" && <i className="bi bi-gear-fill"></i>}
                            {order.order_status === "shipping" && <i className="bi bi-truck"></i>}
                            {order.order_status === "delivered" && <i className="bi bi-check2-circle"></i>}
                            {order.order_status === "cancel" && <i className="bi bi-x-circle-fill"></i>}
                            {order.order_status}
                          </span>
                        </td>

                        {/* PAYMENT STATUS */}
                        <td>
                          <div className="payment-status-wrapper">
                            <span className={getPaymentStatusClass(order.payment_status)}>
                              {order.payment_status === "success" || order.payment_status === "paid" ? (
                                <><i className="bi bi-check-circle-fill"></i> Paid</>
                              ) : order.payment_status === "failed" ? (
                                <><i className="bi bi-exclamation-circle-fill"></i> Failed</>
                              ) : (
                                <><i className="bi bi-hourglass-split"></i> Pending</>
                              )}
                            </span>

                            <select
                              className="table-inner-select"
                              value={order.payment_status || "pending"}
                              disabled={actionLoading === order.id}
                              onChange={(e) => updatePaymentStatus(order.id, e.target.value)}
                            >
                              <option value="pending">Pending</option>
                              <option value="success">Success</option>
                              <option value="failed">Failed</option>
                            </select>
                          </div>
                        </td>

                        {/* PAYMENT METHOD & TRANSACTION REF */}
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                            <span className={`method-pill ${order.payment_method}`}>
                              {order.payment_method === "online" ? (
                                <><i className="bi bi-credit-card-2-front"></i> Online</>
                              ) : (
                                <><i className="bi bi-cash-stack"></i> COD</>
                              )}
                            </span>
                            {order.payment_id && (
                              <span style={{ fontSize: "11px", color: "#64748b", fontFamily: "monospace", fontWeight: "600" }} title="Razorpay Payment ID">
                                {order.payment_id}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* ACTIONS */}
                        <td>
                          <div className="table-actions-row">
                            <button
                              type="button"
                              className="action-btn-view"
                              disabled={actionLoading === order.id}
                              onClick={() =>
                                setExpandedOrder(
                                  expandedOrder === order.id ? null : order.id
                                )
                              }
                              title={expandedOrder === order.id ? "Hide Details" : "View Details"}
                            >
                              <i className={expandedOrder === order.id ? "bi bi-eye-slash-fill" : "bi bi-eye-fill"}></i>
                            </button>

                            <button
                              type="button"
                              className="action-btn-receipt"
                              onClick={() => setViewingReceiptOrder(order)}
                              title="View & Print Official Receipt"
                            >
                              <i className="bi bi-receipt"></i>
                              <span>Receipt</span>
                            </button>


                            <select
                              className="table-inner-select"
                              disabled={actionLoading === order.id}
                              value={order.order_status}
                              onChange={(e) => updateStatus(order.id, e.target.value)}
                            >
                              <option value="pending">Pending</option>
                              <option value="processing">Processing</option>
                              <option value="shipping">Shipping</option>
                              <option value="delivered">Delivered</option>
                              <option value="cancel">Cancel</option>
                            </select>

                            <button
                              type="button"
                              className="action-btn-delete"
                              disabled={actionLoading === order.id}
                              onClick={() => deleteOrder(order.id)}
                              title="Delete Order"
                            >
                              {actionLoading === order.id ? (
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                              ) : (
                                <i className="bi bi-trash-fill"></i>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* ================= EXPANDED DETAILS ================= */}
                      {expandedOrder === order.id && (
                        <tr className="details-expanded-row">
                          <td colSpan="9">
                            <div className="order-details-wrapper">
                              {/* CUSTOMER DETAILS CARD */}
                              <div className="detail-card">
                                <h4>
                                  <i className="bi bi-person-badge-fill"></i> Customer Details
                                </h4>

                                <div className="detail-item-grid">
                                  <div className="detail-item-row">
                                    <span className="detail-label">Name</span>
                                    <span className="detail-val">{order.name}</span>
                                  </div>

                                  <div className="detail-item-row">
                                    <span className="detail-label">Email</span>
                                    <span className="detail-val">{order.email}</span>
                                  </div>

                                  <div className="detail-item-row">
                                    <span className="detail-label">Phone</span>
                                    <span className="detail-val">{order.phone}</span>
                                  </div>

                                  <div className="detail-item-row">
                                    <span className="detail-label">City</span>
                                    <span className="detail-val">{order.city || "N/A"}</span>
                                  </div>

                                  <div className="detail-item-row">
                                    <span className="detail-label">Pincode</span>
                                    <span className="detail-val">{order.pincode || "N/A"}</span>
                                  </div>

                                  <div className="detail-item-row">
                                    <span className="detail-label">Address</span>
                                    <span className="detail-val">{order.address}</span>
                                  </div>

                                  {order.shipping_location && (
                                    <div className="detail-item-row">
                                      <span className="detail-label">Shipping Location</span>
                                      <span className="detail-val">{order.shipping_location}</span>
                                    </div>
                                  )}

                                  {order.shipping_charge !== undefined && order.shipping_charge !== null && (
                                    <div className="detail-item-row">
                                      <span className="detail-label">Shipping Cost</span>
                                      <span className="detail-val">₹{order.shipping_charge}</span>
                                    </div>
                                  )}

                                  {order.discount_amount !== undefined && order.discount_amount !== null && Number(order.discount_amount) > 0 && (
                                    <div className="detail-item-row">
                                      <span className="detail-label">Bulk Discount</span>
                                      <span className="detail-val text-success">
                                        -₹{order.discount_amount} ({order.discount_percent}%)
                                      </span>
                                    </div>
                                  )}

                                  {order.platform_fee !== undefined && order.platform_fee !== null && Number(order.platform_fee) > 0 && (
                                    <div className="detail-item-row">
                                      <span className="detail-label">Platform Fee</span>
                                      <span className="detail-val">₹{parseFloat(order.platform_fee).toFixed(2)}</span>
                                    </div>
                                  )}

                                  <div className="detail-item-row">
                                    <span className="detail-label">Payment Method</span>
                                    <span className="detail-val">
                                      {order.payment_method === "online" ? "Online Payment" : "Cash on Delivery (COD)"}
                                    </span>
                                  </div>

                                  <div className="detail-item-row">
                                    <span className="detail-label">Payment Status</span>
                                    <span className="detail-val" style={{ textTransform: "capitalize", fontWeight: "bold" }}>
                                      {order.payment_status || "pending"}
                                    </span>
                                  </div>

                                  <div className="detail-item-row" style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px dashed #cbd5e1" }}>
                                    <span className="detail-label" style={{ fontWeight: "750" }}>POD Number</span>
                                    <span className="detail-val" style={{ display: "flex", gap: "8px", alignItems: "center", width: "70%" }}>
                                      <input
                                        type="text"
                                        placeholder="POD Tracking Number"
                                        style={{
                                          padding: "5px 8px",
                                          borderRadius: "6px",
                                          border: "1px solid #cbd5e1",
                                          fontSize: "12.5px",
                                          flex: 1,
                                          height: "30px",
                                        }}
                                        defaultValue={order.pod_number || ""}
                                        id={`pod-input-${order.id}`}
                                      />
                                      <button
                                        type="button"
                                        disabled={actionLoading === order.id}
                                        style={{
                                          background: actionLoading === order.id ? "#9ca3af" : "#5d1d2b",
                                          color: "#ffffff",
                                          border: "none",
                                          padding: "0 14px",
                                          borderRadius: "6px",
                                          fontSize: "12px",
                                          fontWeight: "bold",
                                          cursor: actionLoading === order.id ? "not-allowed" : "pointer",
                                          height: "30px",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "6px",
                                          transition: "all 0.2s ease",
                                          boxShadow: "0 2px 4px rgba(93, 29, 43, 0.2)"
                                        }}
                                        onClick={async () => {
                                          const inputEl = document.getElementById(`pod-input-${order.id}`);
                                          const val = inputEl ? inputEl.value.trim() : "";
                                          await updatePodNumber(order.id, val);
                                        }}
                                      >
                                        {actionLoading === order.id ? (
                                          <>
                                            <span
                                              className="spinner-border spinner-border-sm"
                                              style={{ width: "12px", height: "12px", borderWidth: "2px" }}
                                              role="status"
                                            ></span>
                                            <span>Saving...</span>
                                          </>
                                        ) : (
                                          <>
                                            <i className="bi bi-check2"></i>
                                            <span>Save</span>
                                          </>
                                        )}
                                      </button>
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* ORDER ITEMS CARD */}
                              <div className="detail-card">
                                <h4>
                                  <i className="bi bi-box-seam-fill"></i> Order Items
                                </h4>

                                <div className="order-items-column">
                                  {order.items?.map((item, idx) => (
                                    <div key={idx} className="inspector-product-row">
                                      <div className="product-info-block">
                                        <span className="product-title">{item.product_name}</span>
                                        <span className="product-meta">Qty: {item.qty} × ₹{item.price}</span>
                                      </div>
                                      <div className="product-price-block">
                                        ₹{Number(item.qty) * Number(item.price)}
                                      </div>
                                    </div>
                                  ))}

                                  <div className="order-totals-wrapper">
                                    {order.shipping_charge !== undefined && order.shipping_charge !== null && Number(order.shipping_charge) > 0 && (
                                      <div className="detail-item-row order-total-row-highlight" style={{ paddingBottom: 4 }}>
                                        <span className="detail-label" style={{ fontSize: "13px" }}>Shipping Cost</span>
                                        <span className="detail-val" style={{ fontSize: "13px" }}>₹{order.shipping_charge}</span>
                                      </div>
                                    )}
                                    {order.discount_amount !== undefined && order.discount_amount !== null && Number(order.discount_amount) > 0 && (
                                      <div className="detail-item-row order-total-row-highlight" style={{ paddingBottom: 4 }}>
                                        <span className="detail-label" style={{ fontSize: "13px" }}>Bulk Discount</span>
                                        <span className="detail-val text-success" style={{ fontSize: "13px" }}>-₹{order.discount_amount}</span>
                                      </div>
                                    )}
                                    {order.platform_fee !== undefined && order.platform_fee !== null && Number(order.platform_fee) > 0 && (
                                      <div className="detail-item-row order-total-row-highlight" style={{ paddingBottom: 4 }}>
                                        <span className="detail-label" style={{ fontSize: "13px" }}>Platform Fee</span>
                                        <span className="detail-val" style={{ fontSize: "13px" }}>₹{parseFloat(order.platform_fee).toFixed(2)}</span>
                                      </div>
                                    )}
                                    <div className="detail-item-row order-total-row-highlight" style={{ paddingTop: 4 }}>
                                      <span className="detail-label" style={{ fontSize: "15px", color: "#1e293b" }}>Grand Total</span>
                                      <span className="detail-val" style={{ fontSize: "17px", fontWeight: "800", color: "#bc9341" }}>
                                        ₹{order.total_amount}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9">
                      <div className="orders-empty-state">
                        <i className="bi bi-inbox"></i>
                        <h3>No Orders Found</h3>
                        <p>Try adjusting your search query or filter criteria.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ================= PAGINATION ================= */}
          {totalPages > 1 && (
            <div className="orders-pagination">
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-nav-btn"
              >
                <i className="bi bi-chevron-left"></i> Previous
              </button>

              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => goToPage(page)}
                    className={`page-num-btn ${currentPage === page ? "active" : ""}`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-nav-btn"
              >
                Next <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}
        </section>
      )}

      {/* ================= PRINT LABELS MODAL PORTAL ================= */}
      {showPrintLabelModal && createPortal(
        <div className="print-label-overlay" onClick={() => setShowPrintLabelModal(false)}>
          <style>{`
            @keyframes fadeInLabelOverlay {
              from { opacity: 0; backdrop-filter: blur(0px); }
              to { opacity: 1; backdrop-filter: blur(6px); }
            }
            @keyframes slideUpLabelContainer {
              from { transform: translateY(30px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
            .print-label-overlay {
              position: fixed;
              top: 0;
              left: 0;
              width: 100vw;
              height: 100vh;
              background-color: rgba(24, 8, 10, 0.5);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 99999;
              animation: fadeInLabelOverlay 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            }
            .print-label-container {
              background-color: #ffffff;
              border-radius: 16px;
              width: 94%;
              max-width: 580px;
              max-height: 92vh;
              overflow-y: auto;
              box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
              animation: slideUpLabelContainer 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              border: 1px solid rgba(0, 0, 0, 0.08);
              font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            }
            .print-label-header {
              background: linear-gradient(135deg, #4a1216 0%, #6b1d24 100%);
              padding: 18px 24px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              border-bottom: 2px solid #dfb76c;
            }
            .print-label-title {
              color: #ffffff;
              margin: 0;
              font-size: 1.15rem;
              font-weight: 750;
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .print-label-title i {
              color: #dfb76c;
            }
            .print-label-close {
              background: transparent;
              border: none;
              color: rgba(255, 255, 255, 0.8);
              font-size: 1.6rem;
              cursor: pointer;
              transition: all 0.2s;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 32px;
              height: 32px;
              border-radius: 50%;
            }
            .print-label-close:hover {
              background-color: rgba(255, 255, 255, 0.1);
              color: #ffffff;
              transform: rotate(90deg);
            }
            .print-label-body {
              padding: 22px 24px;
              text-align: left;
            }
            .print-label-group {
              display: flex;
              flex-direction: column;
              gap: 6px;
              margin-bottom: 18px;
            }
            .print-label-group:last-child {
              margin-bottom: 0;
            }
            .print-label-group label {
              font-size: 0.82rem;
              font-weight: 650;
              color: #4a5568;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .date-scope-pills {
              display: flex;
              flex-wrap: wrap;
              gap: 6px;
            }
            .scope-pill {
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 0.78rem;
              font-weight: 600;
              border: 1px solid #e2e8f0;
              background-color: #f8fafc;
              color: #475569;
              cursor: pointer;
              transition: all 0.2s ease;
            }
            .scope-pill:hover {
              background-color: #f1f5f9;
              color: #0f172a;
              border-color: #cbd5e1;
            }
            .scope-pill.active {
              background: linear-gradient(135deg, #58181f 0%, #701e24 100%);
              color: #ffffff;
              border-color: #58181f;
              box-shadow: 0 2px 6px rgba(88, 24, 31, 0.25);
            }
            .print-label-preview-badge {
              background: #fff8f8;
              border: 1px solid #fecaca;
              border-radius: 10px;
              padding: 10px 14px;
              margin-bottom: 18px;
            }
            .preview-badge-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .badge-chip {
              font-size: 0.85rem;
              color: #701e24;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .preview-breakdown {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              margin-top: 6px;
            }
            .breakdown-tag {
              font-size: 0.75rem;
              font-weight: 650;
              padding: 2px 8px;
              border-radius: 6px;
            }
            .breakdown-tag.subs {
              background: #eef2ff;
              color: #3730a3;
              border: 1px solid #c7d2fe;
            }
            .breakdown-tag.orders {
              background: #f0fdf4;
              color: #166534;
              border: 1px solid #bbf7d0;
            }
            .preview-zero-notice {
              margin: 6px 0 0 0;
              font-size: 0.78rem;
              color: #991b1b;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .member-selection-card {
              border: 1px solid #e2e8f0;
              border-radius: 10px;
              background: #ffffff;
              padding: 12px;
              margin-bottom: 18px;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
            }
            .member-selection-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 10px;
              flex-wrap: wrap;
              gap: 8px;
            }
            .member-selection-title {
              font-size: 0.84rem;
              font-weight: 700;
              color: #1e293b;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .selection-count-pill {
              font-size: 0.72rem;
              font-weight: 650;
              background: #f1f5f9;
              color: #475569;
              padding: 2px 8px;
              border-radius: 12px;
              margin-left: 4px;
            }
            .member-selection-actions {
              display: flex;
              gap: 6px;
            }
            .btn-select-action {
              padding: 4px 10px;
              font-size: 0.74rem;
              font-weight: 600;
              border: 1px solid #cbd5e1;
              border-radius: 6px;
              background: #f8fafc;
              color: #334155;
              cursor: pointer;
              transition: all 0.15s;
              display: flex;
              align-items: center;
              gap: 4px;
            }
            .btn-select-action:hover {
              background: #e2e8f0;
              color: #0f172a;
              border-color: #94a3b8;
            }
            .member-search-box {
              position: relative;
              margin-bottom: 8px;
            }
            .member-search-box i {
              position: absolute;
              left: 10px;
              top: 50%;
              transform: translateY(-50%);
              font-size: 0.8rem;
              color: #94a3b8;
            }
            .member-search-box input {
              width: 100%;
              padding: 7px 28px 7px 30px;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              font-size: 0.8rem;
              box-sizing: border-box;
              transition: border 0.15s;
            }
            .member-search-box input:focus {
              outline: none;
              border-color: #701e24;
              box-shadow: 0 0 0 2px rgba(112, 30, 36, 0.1);
            }
            .member-search-clear {
              position: absolute;
              right: 8px;
              top: 50%;
              transform: translateY(-50%);
              border: none;
              background: none;
              color: #94a3b8;
              font-size: 1.1rem;
              cursor: pointer;
              line-height: 1;
              padding: 0 4px;
            }
            .member-search-clear:hover {
              color: #334155;
            }
            .member-list-scroll {
              max-height: 220px;
              overflow-y: auto;
              border: 1px solid #f1f5f9;
              border-radius: 6px;
              background: #fafafa;
            }
            .member-row-item {
              display: flex;
              align-items: center;
              gap: 10px;
              padding: 8px 10px;
              border-bottom: 1px solid #edf2f7;
              cursor: pointer;
              transition: background 0.15s;
              font-size: 0.8rem;
              user-select: none;
            }
            .member-row-item:last-child {
              border-bottom: none;
            }
            .member-row-item:hover {
              background: #f1f5f9;
            }
            .member-row-item.selected {
              background: #fdf5f5;
            }
            .member-row-item input[type="checkbox"] {
              cursor: pointer;
              accent-color: #701e24;
              width: 16px;
              height: 16px;
              flex-shrink: 0;
            }
            .member-col-id {
              font-weight: 700;
              color: #1e293b;
              font-size: 0.8rem;
              white-space: nowrap;
              min-width: 68px;
              flex-shrink: 0;
            }
            .member-col-info {
              flex: 1;
              min-width: 0;
              display: flex;
              flex-direction: column;
              gap: 2px;
            }
            .member-col-name {
              font-weight: 650;
              color: #0f172a;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .member-col-sub {
              font-size: 0.72rem;
              color: #64748b;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .member-col-tag-wrap {
              flex-shrink: 0;
            }
            .member-col-tag {
              font-size: 0.7rem;
              padding: 2px 7px;
              border-radius: 4px;
              font-weight: 650;
              white-space: nowrap;
              display: inline-flex;
              align-items: center;
              gap: 4px;
            }
            .member-tag-renewal {
              background: #fef3c7;
              color: #92400e;
              border: 1px solid #fde68a;
            }
            .member-tag-new {
              background: #dcfce7;
              color: #166534;
              border: 1px solid #bbf7d0;
            }
            .member-tag-order {
              background: #e0f2fe;
              color: #0369a1;
              border: 1px solid #bae6fd;
            }
            .member-empty-search {
              padding: 24px 12px;
              text-align: center;
              color: #94a3b8;
              font-size: 0.8rem;
            }
            .print-label-input-wrapper {
              position: relative;
              display: flex;
              align-items: center;
            }
            .print-label-input-wrapper i {
              position: absolute;
              left: 12px;
              color: #a0aec0;
              font-size: 0.95rem;
            }
            .print-label-input-wrapper input,
            .print-label-input-wrapper select {
              width: 100%;
              padding: 10px 12px 10px 38px;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              font-size: 0.9rem;
              color: #2d3748;
              background-color: #ffffff;
              transition: all 0.2s;
              box-sizing: border-box;
            }
            .print-label-input-wrapper input:focus,
            .print-label-input-wrapper select:focus {
              outline: none;
              border-color: #6b1d24;
              box-shadow: 0 0 0 3px rgba(107, 29, 36, 0.15);
            }
            .print-label-footer {
              padding: 16px 24px;
              background-color: #fdfdfd;
              border-top: 1px solid #ebdcb9;
              display: flex;
              justify-content: flex-end;
              gap: 12px;
            }
            .btn-label-cancel {
              padding: 10px 20px;
              border: 1px solid #cbd5e1;
              background-color: #ffffff;
              color: #475569;
              border-radius: 8px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
            }
            .btn-label-cancel:hover {
              background-color: #f1f5f9;
              color: #1e293b;
            }
            .btn-label-submit {
              padding: 10px 24px;
              border: none;
              background: linear-gradient(135deg, #58181f 0%, #701e24 100%);
              color: #ffffff;
              border-radius: 8px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
              box-shadow: 0 4px 10px rgba(88, 24, 31, 0.25);
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .btn-label-submit:hover {
              transform: translateY(-1px);
              box-shadow: 0 6px 14px rgba(88, 24, 31, 0.35);
              background: linear-gradient(135deg, #4c141a 0%, #631a20 100%);
            }
          `}</style>
          
          <div className="print-label-container" onClick={(e) => e.stopPropagation()}>
            <div className="print-label-header">
              <h3 className="print-label-title">
                <i className="bi bi-tags-fill"></i> Print Shipping Labels
              </h3>
              <button className="print-label-close" onClick={() => setShowPrintLabelModal(false)}>&times;</button>
            </div>
            
            <div className="print-label-body">
              {/* 1. DISPATCH DATE SCOPE PILLS */}
              <div className="print-label-group">
                <label><i className="bi bi-calendar3"></i> Dispatch Date Filter</label>
                <div className="date-scope-pills">
                  <button
                    type="button"
                    className={`scope-pill ${printDateScope === "today" ? "active" : ""}`}
                    onClick={() => {
                      setPrintDateScope("today");
                    }}
                  >
                    🌟 Today ({getTodayISO().split("-").reverse().join("-")})
                  </button>
                  <button
                    type="button"
                    className={`scope-pill ${printDateScope === "yesterday" ? "active" : ""}`}
                    onClick={() => {
                      setPrintDateScope("yesterday");
                    }}
                  >
                    Yesterday
                  </button>
                  <button
                    type="button"
                    className={`scope-pill ${printDateScope === "specific_date" ? "active" : ""}`}
                    onClick={() => {
                      setPrintDateScope("specific_date");
                    }}
                  >
                    📅 Specific Date
                  </button>
                  <button
                    type="button"
                    className={`scope-pill ${printDateScope === "date_range" ? "active" : ""}`}
                    onClick={() => {
                      setPrintDateScope("date_range");
                    }}
                  >
                    📆 Date Range
                  </button>
                  <button
                    type="button"
                    className={`scope-pill ${printDateScope === "all" ? "active" : ""}`}
                    onClick={() => {
                      setPrintDateScope("all");
                    }}
                  >
                    🖨️ All Records (Monthly)
                  </button>
                </div>
              </div>

              {/* SPECIFIC DATE PICKER */}
              {printDateScope === "specific_date" && (
                <div className="print-label-group">
                  <label><i className="bi bi-calendar-event"></i> Select Target Date</label>
                  <div className="print-label-input-wrapper">
                    <i className="bi bi-calendar-check"></i>
                    <input
                      type="date"
                      value={printTargetDate}
                      onChange={(e) => {
                        setPrintTargetDate(e.target.value);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* DATE RANGE PICKERS */}
              {printDateScope === "date_range" && (
                <div className="print-label-group" style={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.78rem' }}>From Date</label>
                    <input
                      type="date"
                      value={printFromDate}
                      onChange={(e) => setPrintFromDate(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.78rem' }}>To Date</label>
                    <input
                      type="date"
                      value={printToDate}
                      onChange={(e) => {
                        setPrintToDate(e.target.value);
                      }}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>
              )}

              {/* 2. SHIPPING CATEGORY FILTER */}
              <div className="print-label-group">
                <label><i className="bi bi-funnel"></i> Select Shipping Category</label>
                <div className="print-label-input-wrapper">
                  <i className="bi bi-funnel"></i>
                  <select
                    value={printLabelFilter}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                  >
                    <optgroup label="🌟 Daily Dispatch (By Date)">
                      <option value="daily_combined">🚚 Subscriptions & Book Orders Combined</option>
                      <option value="daily_subs">📚 Magazine Subscriptions Only (New & Renewals)</option>
                      <option value="daily_orders">📦 Book Store Orders Only</option>
                    </optgroup>
                    <optgroup label="🏛️ Monthly Mass Dispatch (Full Run)">
                      <option value="sub_paid_active">Active Paid Subscribers (All Active)</option>
                      <option value="orders_paid">All Book Store Orders (Ready)</option>
                      <option value="sub_all">All Registered Subscribers (Full Database)</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* 3. DISPATCH / DELIVERY MODE FILTER */}
              {labelPreview.includeSubs && (
                <div className="print-label-group">
                  <label><i className="bi bi-truck"></i> Select Dispatch / Delivery Mode</label>
                  <div className="print-label-input-wrapper">
                    <i className="bi bi-truck"></i>
                    <select
                      value={printDispatchFilter}
                      onChange={(e) => setPrintDispatchFilter(e.target.value)}
                      disabled={loadingSubscribersCache}
                    >
                      <option value="all">🌐 All Delivery Modes ({loadingSubscribersCache ? "..." : labelPreview.dispatchCounts.all})</option>
                      <option value="general_post">📮 Indian Postal / General Post ({loadingSubscribersCache ? "..." : labelPreview.dispatchCounts.general_post})</option>
                      <option value="speed_post">⚡ Indian Postal / Speed Post ({loadingSubscribersCache ? "..." : labelPreview.dispatchCounts.speed_post})</option>
                      <option value="book_post">📚 Indian Postal / Book Post ({loadingSubscribersCache ? "..." : labelPreview.dispatchCounts.book_post})</option>
                      <option value="courier">🚚 Courier Service ({loadingSubscribersCache ? "..." : labelPreview.dispatchCounts.courier})</option>
                      <option value="direct">🤝 Direct / Hand Delivery ({loadingSubscribersCache ? "..." : labelPreview.dispatchCounts.direct})</option>
                      <option value="air_mail">✈️ Air Mail / International Post ({loadingSubscribersCache ? "..." : labelPreview.dispatchCounts.air_mail})</option>
                    </select>
                  </div>
                </div>
              )}



              {/* 5. LIVE COUNT PREVIEW BADGE */}
              <div className="print-label-preview-badge">
                <div className="preview-badge-header">
                  <span className="badge-chip">
                    {loadingSubscribersCache ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2 text-primary" role="status" aria-hidden="true" style={{ width: '13px', height: '13px', display: 'inline-block' }}></span>
                        Loading subscriber records...
                      </>
                    ) : labelPreview.totalCount > 0 ? (
                      <>
                        <i className="bi bi-check-circle-fill text-success"></i>
                        Found <strong>{labelPreview.totalCount}</strong> labels to print ({labelPreview.totalSheets} {labelPreview.totalSheets === 1 ? 'Sheet' : 'Sheets'})
                      </>
                    ) : (
                      <>
                        <i className="bi bi-exclamation-circle text-danger"></i>
                        No records found for selected filter
                      </>
                    )}
                  </span>
                </div>
                {!loadingSubscribersCache && labelPreview.totalCount > 0 ? (
                  <div className="preview-breakdown">
                    {labelPreview.includeSubs && (
                      <span className="breakdown-tag subs">
                        📚 {labelPreview.subsCount} Subscriptions
                        {labelPreview.renewalsCount > 0 ? (
                          <span style={{ fontSize: '0.74rem', marginLeft: '5px', opacity: 0.88, fontWeight: 'normal' }}>
                            ({labelPreview.newSubsCount > 0 ? `${labelPreview.newSubsCount} New, ` : ""}{labelPreview.renewalsCount} Renewal{labelPreview.renewalsCount > 1 ? "s" : ""})
                          </span>
                        ) : ""}
                      </span>
                    )}
                    {labelPreview.includeOrders && (
                      <span className="breakdown-tag orders">📦 {labelPreview.ordersCount} Book Orders</span>
                    )}
                  </div>
                ) : !loadingSubscribersCache ? (
                  <p className="preview-zero-notice">
                    <i className="bi bi-info-circle"></i> No subscriptions or book orders match this date ({printDateScope === "specific_date" ? printTargetDate.split("-").reverse().join("-") : printDateScope}). Select another date or choose "All Records (Monthly)".
                  </p>
                ) : null}
              </div>

              {/* MEMBER SELECTION CHECKLIST (Hidden for All Records (Monthly)) */}
              {!loadingSubscribersCache && labelPreview.totalCount > 0 && printDateScope !== "all" && (
                <div className="member-selection-card">
                  <div className="member-selection-header">
                    <div className="member-selection-title">
                      <i className="bi bi-person-check-fill" style={{ color: '#701e24' }}></i>
                      Select Members to Print
                      <span className="selection-count-pill">
                        {selectedCount} of {labelPreview.totalCount} selected
                      </span>
                    </div>
                    <div className="member-selection-actions">
                      <button
                        type="button"
                        className="btn-select-action"
                        onClick={() => toggleSelectAll(true)}
                        title="Select all members in list"
                      >
                        <i className="bi bi-check-all"></i> Select All
                      </button>
                      <button
                        type="button"
                        className="btn-select-action"
                        onClick={() => toggleSelectAll(false)}
                        title="Deselect all members"
                      >
                        <i className="bi bi-dash-circle"></i> Clear All
                      </button>
                    </div>
                  </div>

                  {labelPreview.totalCount > 4 && (
                    <div className="member-search-box">
                      <i className="bi bi-search"></i>
                      <input
                        type="text"
                        placeholder="Search by Name, ID, City, or Phone..."
                        value={memberSearchQuery}
                        onChange={(e) => setMemberSearchQuery(e.target.value)}
                      />
                      {memberSearchQuery && (
                        <button
                          type="button"
                          className="member-search-clear"
                          onClick={() => setMemberSearchQuery("")}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  )}

                  <div className="member-list-scroll">
                    {displayedItems.length === 0 ? (
                      <div className="member-empty-search">
                        <i className="bi bi-search"></i> No members match "{memberSearchQuery}"
                      </div>
                    ) : (
                      displayedItems.map((item) => {
                        const isChecked = selectedMemberKeys.includes(item.key);
                        return (
                          <div
                            key={item.key}
                            className={`member-row-item ${isChecked ? "selected" : ""}`}
                            onClick={() => toggleMemberKey(item.key)}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {}} // handled by row onClick
                              onClick={(e) => e.stopPropagation()}
                              onChangeCapture={() => toggleMemberKey(item.key)}
                            />
                            <div className="member-col-id">{item.login_id}</div>
                            <div className="member-col-info">
                              <span className="member-col-name">{item.name}</span>
                              <span className="member-col-sub">
                                {[item.city, item.pincode].filter(Boolean).join(" - ")}
                                {item.phone ? ` • Ph: ${item.phone}` : ""}
                              </span>
                            </div>
                            <div className="member-col-tag-wrap">
                              {item.type === "order" ? (
                                <span className="member-col-tag member-tag-order">
                                  <i className="bi bi-box-seam"></i> Order
                                </span>
                              ) : item.is_renewal ? (
                                <span className="member-col-tag member-tag-renewal">
                                  <i className="bi bi-arrow-repeat"></i> Renewal
                                </span>
                              ) : (
                                <span className="member-col-tag member-tag-new">
                                  <i className="bi bi-star-fill"></i> New
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {/* 6. PRINT BATCH / QUANTITY LIMIT */}
              <div className="print-label-group">
                <label><i className="bi bi-layers"></i> Print Batch / Quantity Limit</label>
                <div className="print-label-input-wrapper">
                  <i className="bi bi-layers"></i>
                  <select
                    value={printBatchLimit}
                    onChange={(e) => setPrintBatchLimit(e.target.value)}
                  >
                    <option value="all">
                      🖨️ {printDateScope === "all"
                            ? `All Labels (${labelPreview.totalSheets} ${labelPreview.totalSheets === 1 ? 'Sheet' : 'Sheets'})`
                            : `All Selected (${selectedSheets} ${selectedSheets === 1 ? 'Sheet' : 'Sheets'})`}
                    </option>
                    <option value="custom">📄 Custom Sheet Range (Select From / To)</option>
                  </select>
                </div>
              </div>

              {printBatchLimit === "custom" && (
                <div className="print-label-group" style={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.78rem' }}>From Sheet #</label>
                    <input
                      type="number"
                      min="1"
                      value={customStartPage}
                      onChange={(e) => setCustomStartPage(e.target.value)}
                      placeholder="1"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.78rem' }}>To Sheet #</label>
                    <input
                      type="number"
                      min="1"
                      value={customEndPage}
                      onChange={(e) => setCustomEndPage(e.target.value)}
                      placeholder={String(labelPreview.totalSheets || 20)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                  </div>
                </div>
              )}


              {/* ALIGNMENT CALIBRATION ACCORDION */}
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '10px 14px',
                marginTop: '12px'
              }}>
                <div 
                  onClick={() => setShowCalibrationSettings(prev => !prev)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="bi bi-sliders2-vertical" style={{ color: '#701e24', fontSize: '0.95rem' }}></i>
                    <span style={{ fontSize: '0.83rem', fontWeight: '700', color: '#1e293b' }}>
                      Sticker Sheet Alignment
                    </span>
                  </div>
                  <span style={{ fontSize: '0.74rem', color: '#701e24', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {showCalibrationSettings ? 'Hide Settings' : `Calibrated (${labelTopMargin}cm / ${labelLeftMargin}cm)`}
                    <i className={`bi bi-chevron-${showCalibrationSettings ? 'up' : 'down'}`}></i>
                  </span>
                </div>

                {showCalibrationSettings && (
                  <div style={{ marginTop: '10px', borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                    <p style={{ fontSize: '0.73rem', color: '#64748b', marginBottom: '8px', lineHeight: '1.3' }}>
                      Calibrated for <strong>21-up Sticker Sheet (20.9cm × 29.81cm)</strong> with 3 × 7 (6.3 × 3.8 cm) labels.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '8px' }}>
                      <div>
                        <label style={{ fontSize: '0.73rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>
                          Top Border: <strong>{labelTopMargin} cm</strong>
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => updateTopMargin(labelTopMargin - 0.05)}
                            style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '6px 0 0 6px', background: '#fff', cursor: 'pointer', fontWeight: 'bold' }}
                          >-</button>
                          <input
                            type="number"
                            step="0.05"
                            value={labelTopMargin}
                            onChange={(e) => updateTopMargin(parseFloat(e.target.value) || 0)}
                            style={{ width: '100%', textAlign: 'center', padding: '3px', border: '1px solid #cbd5e1', borderLeft: 'none', borderRight: 'none', fontSize: '0.82rem' }}
                          />
                          <button
                            type="button"
                            onClick={() => updateTopMargin(labelTopMargin + 0.05)}
                            style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '0 6px 6px 0', background: '#fff', cursor: 'pointer', fontWeight: 'bold' }}
                          >+</button>
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.73rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>
                          Left Border: <strong>{labelLeftMargin} cm</strong>
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => updateLeftMargin(labelLeftMargin - 0.05)}
                            style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '6px 0 0 6px', background: '#fff', cursor: 'pointer', fontWeight: 'bold' }}
                          >-</button>
                          <input
                            type="number"
                            step="0.05"
                            value={labelLeftMargin}
                            onChange={(e) => updateLeftMargin(parseFloat(e.target.value) || 0)}
                            style={{ width: '100%', textAlign: 'center', padding: '3px', border: '1px solid #cbd5e1', borderLeft: 'none', borderRight: 'none', fontSize: '0.82rem' }}
                          />
                          <button
                            type="button"
                            onClick={() => updateLeftMargin(labelLeftMargin + 0.05)}
                            style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '0 6px 6px 0', background: '#fff', cursor: 'pointer', fontWeight: 'bold' }}
                          >+</button>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: '6px' }}>
                      <button
                        type="button"
                        onClick={resetLabelMargins}
                        style={{ border: 'none', background: 'none', color: '#701e24', fontSize: '0.72rem', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                      >
                        Reset defaults (2.35cm / 0.7cm)
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* PRINT SETUP TIP */}
              <div style={{
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '0.75rem',
                color: '#92400e',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                marginTop: '10px'
              }}>
                <i className="bi bi-printer-fill" style={{ color: '#b45309', marginTop: '1px', fontSize: '0.85rem' }}></i>
                <div>
                  <strong>Important Browser Print Dialog Settings:</strong>
                  <ul style={{ margin: '3px 0 0 14px', padding: 0, lineHeight: '1.35' }}>
                    <li>Paper size: <strong>Default (20.9 cm × 29.81 cm Sheet)</strong></li>
                    <li>Margins: <strong>None</strong> (or Custom 0)</li>
                    <li>Scale: <strong>100% / Actual Size</strong> — do NOT choose "Fit to page" or "Shrink to fit"</li>
                    <li>Options: Uncheck <strong>Headers and footers</strong></li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="print-label-footer">
              <button
                type="button"
                onClick={() => setShowPrintLabelModal(false)}
                className="btn-label-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePrintLabels}
                disabled={printingLabels || loadingSubscribersCache || selectedCount === 0}
                className="btn-label-submit"
                style={{
                  opacity: (printingLabels || loadingSubscribersCache || selectedCount === 0) ? 0.65 : 1,
                  cursor: (printingLabels || loadingSubscribersCache || selectedCount === 0) ? 'not-allowed' : 'pointer'
                }}
              >
                <i className="bi bi-printer"></i>{" "}
                {printingLabels
                  ? "Preparing Print Layout..."
                  : loadingSubscribersCache
                  ? "Loading Data..."
                  : printDateScope === "all"
                  ? `Print All ${labelPreview.totalCount} Labels (${labelPreview.totalSheets} ${labelPreview.totalSheets === 1 ? "Sheet" : "Sheets"})`
                  : selectedCount > 0
                  ? `Print ${selectedCount} Labels (${selectedSheets} ${selectedSheets === 1 ? "Sheet" : "Sheets"})`
                  : "Select Members to Print"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ================= OFFICIAL BOOK ORDER RECEIPT MODAL ================= */}
      {viewingReceiptOrder && createPortal(
        <div className="receipt-modal-overlay">
          <div className="receipt-modal-container">
            
            {/* MODAL ACTION BAR */}
            <div className="receipt-modal-header">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-file-earmark-text-fill fs-5" style={{ color: "#d4af37" }}></i>
                <div>
                  <strong style={{ fontSize: "16px", color: "#fff" }}>Official Book Purchase Receipt</strong>
                  <span className="d-block text-white-50" style={{ fontSize: "11px" }}>Order #{viewingReceiptOrder.id} • Vaithikasri Publications</span>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button 
                  type="button" 
                  className="receipt-modal-close-btn"
                  onClick={() => setViewingReceiptOrder(null)}
                  title="Close Modal"
                >
                  ×
                </button>
              </div>
            </div>

            {/* MODAL PRINT BODY */}
            <div className="receipt-modal-body">
              <div className="receipt-print-area" id="official-order-receipt-print">
                <div className="official-receipt-document">
                  
                  {/* WATERMARK BACKGROUND */}
                  <div className="receipt-watermark">VAITHIKASRI BOOKS</div>

                  {/* TOP BRAND HEADER */}
                  <div className="receipt-top-banner">
                    <div className="receipt-brand-identity">
                      <img 
                        src="/logo192.png" 
                        alt="Vaithikasri Logo" 
                        className="receipt-brand-logo" 
                        onError={(e) => { e.target.onerror = null; e.target.src = "/logo192.png"; }}
                      />
                      <div className="receipt-brand-text">
                        <span className="brand-name">VAITHIKASRI</span>
                        <span className="brand-tamil">வைதிகஸ்ரீ</span>
                        <span className="brand-meta">Spiritual & Cultural Monthly Magazine & Publications</span>
                      </div>
                    </div>

                    <div className="receipt-title-block">
                      <span className="receipt-type-badge">BOOK STORE RECEIPT</span>
                      <div className="receipt-meta-box">
                        <div className="meta-line">
                          <span className="lbl">Order ID:</span>
                          <span className="val highlight">ORD-{viewingReceiptOrder.id}</span>
                        </div>
                        <div className="meta-line">
                          <span className="lbl">Order Date:</span>
                          <span className="val">
                            {viewingReceiptOrder.created_at ? new Date(viewingReceiptOrder.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "N/A"}
                          </span>
                        </div>
                        <div className="meta-line">
                          <span className="lbl">Payment Status:</span>
                          <span className="val highlight" style={{ color: "#15803d", textTransform: "uppercase" }}>
                            {viewingReceiptOrder.payment_status || "SUCCESS / PAID"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="receipt-gold-divider"></div>

                  {/* CUSTOMER & DISPATCH GRID */}
                  <div className="receipt-info-grid">
                    <div className="info-card">
                      <div className="card-header-bar">
                        <i className="bi bi-person-fill me-1"></i> CUSTOMER & DELIVERY DETAILS
                      </div>
                      <div className="card-content-body">
                        <div className="info-detail-row">
                          <span className="dt-label">Customer Name:</span>
                          <span className="dt-value fw-bold" style={{ color: "#5d1d2b" }}>{viewingReceiptOrder.name || "N/A"}</span>
                        </div>
                        <div className="info-detail-row">
                          <span className="dt-label">Contact Phone:</span>
                          <span className="dt-value">{viewingReceiptOrder.phone || "N/A"}</span>
                        </div>
                        <div className="info-detail-row">
                          <span className="dt-label">Email Address:</span>
                          <span className="dt-value">{viewingReceiptOrder.email || "N/A"}</span>
                        </div>
                        <div className="info-detail-row">
                          <span className="dt-label">Delivery Address:</span>
                          <span className="dt-value" style={{ lineHeight: "1.4" }}>
                            {viewingReceiptOrder.address || "N/A"}
                            {viewingReceiptOrder.city ? `, ${viewingReceiptOrder.city}` : ""}
                            {viewingReceiptOrder.state ? `, ${viewingReceiptOrder.state}` : ""}
                            {viewingReceiptOrder.pincode ? ` - ${viewingReceiptOrder.pincode}` : ""}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="info-card">
                      <div className="card-header-bar">
                        <i className="bi bi-credit-card-2-front-fill me-1"></i> PAYMENT & ORDER AUDIT
                      </div>
                      <div className="card-content-body">
                        <div className="info-detail-row">
                          <span className="dt-label">Payment Method:</span>
                          <span className="dt-value fw-bold">
                            {viewingReceiptOrder.payment_method === "online" ? "Online Payment Gateway" : "Cash on Delivery (COD)"}
                          </span>
                        </div>
                        <div className="info-detail-row">
                          <span className="dt-label">Payment Status:</span>
                          <span className="dt-value fw-bold" style={{ color: viewingReceiptOrder.payment_status === "success" || viewingReceiptOrder.payment_status === "paid" ? "#166534" : "#b45309" }}>
                            {viewingReceiptOrder.payment_status === "success" || viewingReceiptOrder.payment_status === "paid" ? "✅ PAID (Verified Online)" : viewingReceiptOrder.payment_status}
                          </span>
                        </div>
                        {viewingReceiptOrder.payment_id && (
                          <div className="info-detail-row">
                            <span className="dt-label">Razorpay Ref ID:</span>
                            <span className="dt-value fw-bold" style={{ fontFamily: "monospace", color: "#166534" }}>
                              {viewingReceiptOrder.payment_id}
                            </span>
                          </div>
                        )}
                        <div className="info-detail-row">
                          <span className="dt-label">Order Fulfillment:</span>
                          <span className="dt-value fw-bold" style={{ textTransform: "capitalize", color: "#1e293b" }}>
                            {viewingReceiptOrder.order_status || "Processing"}
                          </span>
                        </div>
                        <div className="info-detail-row">
                          <span className="dt-label">Courier Partner:</span>
                          <span className="dt-value">India Post (Speed Post) / Registered Parcel</span>
                        </div>
                        {viewingReceiptOrder.pod_number && (
                          <div className="info-detail-row">
                            <span className="dt-label">Consignment POD:</span>
                            <span className="dt-value fw-bold text-success" style={{ fontFamily: "monospace" }}>
                              {viewingReceiptOrder.pod_number}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ITEMIZATION TABLE */}
                  <div className="receipt-table-wrapper">
                    <table className="official-receipt-table">
                      <thead>
                        <tr>
                          <th style={{ width: "5%", textAlign: "center" }}>#</th>
                          <th style={{ width: "55%" }}>Book Publication Title & Details</th>
                          <th style={{ width: "15%", textAlign: "center" }}>Unit Price</th>
                          <th style={{ width: "10%", textAlign: "center" }}>Qty</th>
                          <th style={{ width: "15%", textAlign: "right" }}>Total Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parseOrderItems(viewingReceiptOrder).map((item, idx) => (
                          <tr key={idx}>
                            <td className="text-center fw-bold" style={{ color: "#5d1d2b" }}>{idx + 1}</td>
                            <td>
                              <div className="table-item-title">{item.product_name || item.title || "Book Publication"}</div>
                              <div className="table-item-sub">• Genuine Spiritual Book Publication by Vaithikasri</div>
                            </td>
                            <td className="text-center">₹{Number(item.price || 0).toLocaleString("en-IN")}.00</td>
                            <td className="text-center fw-bold">{item.qty || item.quantity || 1}</td>
                            <td className="text-end fw-bold">
                              ₹{(Number(item.price || 0) * Number(item.qty || item.quantity || 1)).toLocaleString("en-IN")}.00
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {viewingReceiptOrder.shipping_charge !== undefined && viewingReceiptOrder.shipping_charge !== null && Number(viewingReceiptOrder.shipping_charge) > 0 && (
                          <tr className="postage-row">
                            <td colSpan="4" className="text-end fw-bold">Shipping & Courier Delivery Charges</td>
                            <td className="text-end fw-bold">₹{Number(viewingReceiptOrder.shipping_charge).toLocaleString("en-IN")}.00</td>
                          </tr>
                        )}
                        {viewingReceiptOrder.discount_amount !== undefined && viewingReceiptOrder.discount_amount !== null && Number(viewingReceiptOrder.discount_amount) > 0 && (
                          <tr className="postage-row">
                            <td colSpan="4" className="text-end fw-bold">Bulk Purchase Discount</td>
                            <td className="text-end text-success fw-bold">-₹{Number(viewingReceiptOrder.discount_amount).toLocaleString("en-IN")}.00</td>
                          </tr>
                        )}
                        {viewingReceiptOrder.platform_fee !== undefined && viewingReceiptOrder.platform_fee !== null && Number(viewingReceiptOrder.platform_fee) > 0 && (
                          <tr className="postage-row">
                            <td colSpan="4" className="text-end fw-bold">Platform Handling Fee</td>
                            <td className="text-end fw-bold">₹{Number(viewingReceiptOrder.platform_fee).toLocaleString("en-IN")}.00</td>
                          </tr>
                        )}
                        <tr className="grand-total-row">
                          <td colSpan="3" className="words-col">
                            <span className="words-label">Amount in Words:</span>
                            <span className="words-val">{convertNumberToWords(viewingReceiptOrder.total_amount)}</span>
                          </td>
                          <td className="text-end total-label">Total Paid:</td>
                          <td className="text-end total-val">₹{Number(viewingReceiptOrder.total_amount || 0).toLocaleString("en-IN")}.00</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* FOOTER & OFFICIAL SEAL */}
                  <div className="receipt-bottom-grid">
                    <div className="receipt-terms-box">
                      <h6 className="terms-title"><i className="bi bi-geo-alt-fill me-1"></i> Publication & Dispatch Office:</h6>
                      <p className="m-0" style={{ fontSize: "12px", color: "#333", lineHeight: "1.6" }}>
                        <strong>Vaithikasri Publications LLP</strong><br />
                        New No: 488, Old No 175, T.T.K. Road, Alwarpet, Chennai - 600018,<br />
                        <span style={{ whiteSpace: "nowrap" }}>Tamil Nadu, India.</span><br />
                        <strong>Contact:</strong> +91 7598856176 / 8903856176 / +91 4424361210<br />
                        <strong>Email:</strong> vaidikasri@gmail.com | <strong>Website:</strong> www.vaithikasri.com
                      </p>
                    </div>

                    <div className="receipt-signature-box">
                      <div className="official-stamp-seal">
                        <div className="seal-inner">
                          <span className="seal-star">★ ★ ★</span>
                          <span className="seal-center">VAITHIKASRI<br />OFFICIAL</span>
                          <span className="seal-check">✓</span>
                        </div>
                      </div>
                      <div className="signatory-text">
                        <p className="m-0 fw-bold text-maroon" style={{ fontSize: "12px" }}>Vaithikasri Publications</p>
                        <p className="m-0 text-muted" style={{ fontSize: "10.5px" }}>Authorized Electronic Receipt</p>
                      </div>
                    </div>
                  </div>

                  {/* BOTTOM DECORATIVE FOOTER */}
                  <div className="receipt-bottom-bar">
                    <span>Vaithikasri, New No: 488, Old No 175, T.T.K. Road, Alwarpet, Chennai - 600018 • Contact: +91 7598856176 / 8903856176 / +91 4424361210 • Email: vaidikasri@gmail.com</span>
                  </div>

                </div>
              </div>
            </div>
            
            <div className="receipt-modal-footer">
              <button 
                type="button"
                className="receipt-btn-close" 
                onClick={() => setViewingReceiptOrder(null)}
              >
                Close
              </button>
              <button 
                type="button"
                className="receipt-btn-print" 
                onClick={() => window.print()}
              >
                <i className="bi bi-printer-fill me-1"></i> Print Receipt / Save PDF
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}
    </div>
  );

};

export default AdminOrders;
